import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, CheckCircle2, ClipboardCheck, Loader2, MapPin, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { KawaTechLogo } from '../branding/kawatech';

const initialCustomer = {
  type: 'FISICA',
  name: '',
  companyName: '',
  document: '',
  email: '',
  phone: '',
  postalCode: '',
  addressLine: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  stateCode: '',
  branchId: '',
};

const steps = ['Cliente', 'Endereco', 'Plano', 'Confirmacao'];

function onlyDigits(value) {
  return value.replace(/\D/g, '');
}

function normalizePlanPrice(plan) {
  if (plan.priceLabel) return plan.priceLabel;
  const price = plan.priceMonthly ?? plan.price ?? plan.amount ?? plan.value ?? plan.monthlyPrice;
  return typeof price === 'number'
    ? price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;
}

function normalizePlanFeatures(plan) {
  const features = Array.isArray(plan.features) ? plan.features : [];
  const attributes = Array.isArray(plan.attributes) ? plan.attributes : [];
  return [...features, ...attributes]
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.name || item.label || item.value || item.description;
      return null;
    })
    .filter(Boolean)
    .slice(0, 5);
}

const OnboardingPage = () => {
  const [step, setStep] = useState(0);
  const [sessionToken, setSessionToken] = useState('');
  const [customer, setCustomer] = useState(initialCustomer);
  const [serviceCode, setServiceCode] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [protocol, setProtocol] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedContract, setAcceptedContract] = useState(false);
  const [acceptedTruth, setAcceptedTruth] = useState(false);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.servicePlanId) === selectedPlanId),
    [plans, selectedPlanId]
  );

  const canSubmit = Boolean(selectedPlan && acceptedContract && acceptedTruth && !protocol);

  const updateCustomer = (field, value) => {
    setCustomer((current) => ({ ...current, [field]: value }));
  };

  const start = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = await api.startOnboarding({
        type: customer.type,
        name: customer.name,
        companyName: customer.companyName,
        document: onlyDigits(customer.document),
        email: customer.email,
        phone: customer.phone,
      });
      setSessionToken(result.sessionToken);
      setStep(1);
    } catch (error) {
      toast.error(error.message || 'Nao foi possivel iniciar o onboarding');
    } finally {
      setLoading(false);
    }
  };

  const lookupPostalCode = async () => {
    const postalCode = onlyDigits(customer.postalCode);
    if (postalCode.length !== 8) {
      toast.error('Informe um CEP com 8 digitos');
      return;
    }

    setLoading(true);
    try {
      const result = await api.lookupOnboardingPostalCode(postalCode);
      const raw = result.raw || {};
      setCustomer((current) => ({
        ...current,
        postalCode,
        addressLine: result.addressLine?.split(',')[0] || raw.logradouro || raw.street || current.addressLine,
        district: raw.bairro || raw.district || raw.neighborhood || current.district,
        city: raw.localidade || raw.cidade || raw.city || current.city,
        stateCode: (raw.uf || raw.estado || raw.state || current.stateCode).toString().slice(0, 2).toUpperCase(),
      }));
      toast.success('Endereco preenchido pelo CEP');
    } catch (error) {
      toast.error('Nao foi possivel consultar o CEP. Preencha o endereco manualmente.');
    } finally {
      setLoading(false);
    }
  };

  const saveCustomer = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api.saveOnboardingCustomer(sessionToken, {
        ...customer,
        document: onlyDigits(customer.document),
        postalCode: onlyDigits(customer.postalCode),
        stateCode: customer.stateCode.toUpperCase(),
        branchId: customer.branchId.trim(),
      });
      const result = await api.getOnboardingServicePlans(sessionToken);
      setServiceCode(result.serviceCode);
      setPlans(result.plans || []);
      setSelectedPlanId('');
      setStep(2);
    } catch (error) {
      toast.error(error.message || 'Nao foi possivel salvar os dados');
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = async (event) => {
    event.preventDefault();
    if (!selectedPlan) {
      toast.error('Selecione um plano');
      return;
    }

    setLoading(true);
    try {
      await api.selectOnboardingPlan(sessionToken, selectedPlan.servicePlanId);
      setStep(3);
    } catch (error) {
      toast.error(error.message || 'Plano invalido');
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Confirme os aceites obrigatorios antes de finalizar');
      return;
    }

    setLoading(true);
    try {
      const result = await api.submitOnboarding(sessionToken);
      setProtocol(result.protocol || '');
      toast.success('Onboarding enviado');
    } catch (error) {
      toast.error(error.message || 'Nao foi possivel finalizar o onboarding');
    } finally {
      setLoading(false);
    }
  };

  const StepIndicator = () => (
    <div className="mx-auto grid max-w-3xl grid-cols-4 gap-3">
      {steps.map((label, index) => (
        <div key={label} className="flex flex-col items-center gap-2 text-center">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-extrabold ${
              index < step
                ? 'border-emerald-500 bg-emerald-500 text-white'
                : index === step
                  ? 'border-primary bg-primary text-white'
                  : 'border-slate-300 bg-slate-100 text-slate-400'
            }`}
          >
            {index < step ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
          </div>
          <p className={`text-xs font-bold ${index === step ? 'text-primary' : index < step ? 'text-emerald-700' : 'text-slate-400'}`}>{label}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f7fb] text-foreground">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
            <ArrowLeft className="h-4 w-4" />
            Login
          </Link>
          <div className="flex items-center gap-5">
            <KawaTechLogo className="hidden h-8 w-auto sm:block" />
            <div className="flex items-center gap-2">
              <img src="/icon.svg" alt="" className="h-8 w-8" />
              <span className="font-bold text-primary">KawaTenda</span>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid min-h-[calc(100vh-65px)] max-w-6xl gap-8 px-5 py-8 lg:grid-cols-[380px_1fr] lg:py-12">
        <aside className="space-y-8">
          <div>
            <KawaTechLogo className="mb-8 h-9 w-auto" />
            <Badge className="bg-secondary text-secondary-foreground">Onboarding KawaTech</Badge>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight text-primary">
              Contrate o KawaTenda com dados validados
            </h1>
            <p className="mt-4 text-base leading-7 text-primary/70">
              Informe os dados da empresa, valide o endereco, escolha um plano retornado pelo KawaContrato e envie a solicitacao.
            </p>
          </div>
          <div className="grid gap-4">
            {[
              { icon: ShieldCheck, title: 'Sem criacao automatica de usuario', text: 'Contas internas continuam sob controle do administrador.' },
              { icon: MapPin, title: 'CEP com preenchimento manual', text: 'Falhas de consulta nao impedem continuar com endereco digitado.' },
              { icon: ClipboardCheck, title: 'Plano validado', text: 'Somente planos retornados pelo service-plans podem ser enviados.' },
            ].map((item) => (
              <div key={item.title} className="grid grid-cols-[32px_1fr] gap-4">
                <item.icon className="mt-1 h-6 w-6 text-secondary" />
                <div>
                  <h2 className="font-bold text-primary">{item.title}</h2>
                  <p className="mt-1 text-sm leading-6 text-primary/65">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <Card className="self-start rounded-lg border bg-white shadow-sm">
          <CardContent className="space-y-7 p-6 lg:p-8">
            <StepIndicator />

            {step === 0 && (
              <form onSubmit={start} className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2 text-primary">
                    {customer.type === 'JURIDICA' ? <Building2 className="h-6 w-6" /> : <UserRound className="h-6 w-6" />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-primary">Identificacao do cliente</h2>
                    <p className="mt-1 text-sm text-primary/65">Esses dados criam o tenant usado como staging do customer-intake.</p>
                  </div>
                </div>
                <div className="grid rounded-lg border bg-muted p-1 sm:grid-cols-2">
                  <Button type="button" variant={customer.type === 'FISICA' ? 'default' : 'ghost'} onClick={() => updateCustomer('type', 'FISICA')}>Pessoa fisica</Button>
                  <Button type="button" variant={customer.type === 'JURIDICA' ? 'default' : 'ghost'} onClick={() => updateCustomer('type', 'JURIDICA')}>Pessoa juridica</Button>
                </div>
                <div className="rounded-lg border p-4">
                  <h3 className="mb-4 text-sm font-extrabold uppercase text-primary/70">Dados cadastrais</h3>
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={customer.name} onChange={(event) => updateCustomer('name', event.target.value)} required />
                </div>
                {customer.type === 'JURIDICA' && (
                  <div className="mt-4 space-y-2">
                    <Label>Nome fantasia *</Label>
                    <Input value={customer.companyName} onChange={(event) => updateCustomer('companyName', event.target.value)} required />
                  </div>
                )}
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{customer.type === 'JURIDICA' ? 'CNPJ *' : 'CPF *'}</Label>
                    <Input value={customer.document} onChange={(event) => updateCustomer('document', event.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={customer.email} onChange={(event) => updateCustomer('email', event.target.value)} required />
                  </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Telefone *</Label>
                    <Input value={customer.phone} onChange={(event) => updateCustomer('phone', event.target.value)} required />
                  </div>
                </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Continuar
                </Button>
              </form>
            )}

            {step === 1 && (
              <form onSubmit={saveCustomer} className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2 text-primary">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-primary">Contato e endereco</h2>
                    <p className="mt-1 text-sm text-primary/65">Complete o endereco e informe a filial selecionada para o customer-intake.</p>
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <h3 className="mb-4 text-sm font-extrabold uppercase text-primary/70">Endereco</h3>
                <div className="grid gap-4 sm:grid-cols-[1fr_170px]">
                  <div className="space-y-2">
                    <Label>CEP *</Label>
                    <div className="flex gap-2">
                      <Input value={customer.postalCode} onChange={(event) => updateCustomer('postalCode', event.target.value)} required />
                      <Button type="button" variant="outline" onClick={lookupPostalCode} disabled={loading}>Buscar</Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Filial *</Label>
                    <Input value={customer.branchId} onChange={(event) => updateCustomer('branchId', event.target.value)} required />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Label>Endereco *</Label>
                  <Input value={customer.addressLine} onChange={(event) => updateCustomer('addressLine', event.target.value)} required />
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-[120px_1fr]">
                  <div className="space-y-2">
                    <Label>Numero *</Label>
                    <Input value={customer.number} onChange={(event) => updateCustomer('number', event.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Complemento</Label>
                    <Input value={customer.complement} onChange={(event) => updateCustomer('complement', event.target.value)} />
                  </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_90px]">
                  <div className="space-y-2">
                    <Label>Bairro *</Label>
                    <Input value={customer.district} onChange={(event) => updateCustomer('district', event.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade *</Label>
                    <Input value={customer.city} onChange={(event) => updateCustomer('city', event.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>UF *</Label>
                    <Input value={customer.stateCode} onChange={(event) => updateCustomer('stateCode', event.target.value.toUpperCase())} maxLength={2} required />
                  </div>
                </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Buscar planos
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={selectPlan} className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2 text-primary">
                    <ClipboardCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-primary">Consulta e selecao de planos</h2>
                    <p className="mt-1 text-sm text-primary/65">Servico validado: <span className="font-bold">{serviceCode}</span></p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {plans.map((plan) => {
                    const planId = String(plan.servicePlanId);
                    const active = selectedPlanId === planId;
                    const features = normalizePlanFeatures(plan);
                    return (
                      <button
                        type="button"
                        key={planId}
                        onClick={() => setSelectedPlanId(planId)}
                        className={`flex min-h-[220px] flex-col rounded-lg border p-5 text-left transition ${
                          active ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-extrabold text-primary">{plan.name || plan.title || plan.servicePlanId}</h3>
                            {plan.description && <p className="mt-1 text-sm text-primary/65">{plan.description}</p>}
                          </div>
                          {active && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                        </div>
                        {normalizePlanPrice(plan) && <p className="mt-4 text-2xl font-extrabold text-primary">{normalizePlanPrice(plan)}</p>}
                        <ul className="mt-4 space-y-2 text-sm text-primary/70">
                          {features.length > 0 ? features.map((feature) => (
                            <li key={feature} className="flex gap-2">
                              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                              <span>{feature}</span>
                            </li>
                          )) : (
                            <li className="text-primary/50">Recursos conforme contrato do plano.</li>
                          )}
                        </ul>
                        <span className={`mt-auto inline-flex h-9 items-center justify-center rounded-md text-sm font-bold ${
                          active ? 'bg-primary text-white' : 'border border-input text-primary'
                        }`}>
                          {active ? 'Selecionado' : 'Selecionar'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <Button type="submit" className="w-full" disabled={loading || !selectedPlan}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Selecionar plano
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2 text-primary">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-primary">Confirmacao e envio</h2>
                    <p className="mt-1 text-sm text-primary/65">Revise antes de enviar ao customer-intake.</p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border bg-muted/40 p-4 text-sm leading-7">
                    <Badge variant="outline" className="mb-2">Step 1</Badge>
                    <p><strong>Cliente:</strong> {customer.name}</p>
                    <p><strong>Tipo:</strong> {customer.type === 'JURIDICA' ? 'Pessoa juridica' : 'Pessoa fisica'}</p>
                    <p><strong>Documento:</strong> {customer.document ? `****${onlyDigits(customer.document).slice(-4)}` : '-'}</p>
                    <p><strong>Email:</strong> {customer.email}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4 text-sm leading-7">
                    <Badge variant="outline" className="mb-2">Step 2</Badge>
                    <p><strong>Endereco:</strong> {customer.addressLine}, {customer.number}</p>
                    <p><strong>Cidade:</strong> {customer.city}/{customer.stateCode}</p>
                    <p><strong>Filial:</strong> {customer.branchId}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4 text-sm leading-7 md:col-span-2">
                    <Badge variant="outline" className="mb-2">Step 3</Badge>
                    <p><strong>Servico:</strong> {serviceCode}</p>
                    <p><strong>Plano:</strong> {selectedPlan?.name || selectedPlan?.title || selectedPlanId}</p>
                    <p><strong>Valor:</strong> {selectedPlan ? normalizePlanPrice(selectedPlan) || 'Conforme contrato' : '-'}</p>
                  </div>
                </div>
                <div className="space-y-3 rounded-lg border p-4">
                  <label className="flex items-start gap-3 text-sm leading-6 text-primary/75">
                    <Checkbox checked={acceptedContract} onCheckedChange={(value) => setAcceptedContract(Boolean(value))} />
                    <span>Confirmo que os dados cadastrais, endereco, filial e plano selecionado estao corretos.</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm leading-6 text-primary/75">
                    <Checkbox checked={acceptedTruth} onCheckedChange={(value) => setAcceptedTruth(Boolean(value))} />
                    <span>Autorizo o envio para o customer-intake KawaTech com documento sem mascara no payload operacional.</span>
                  </label>
                  <div className="rounded-md bg-primary/5 p-3 text-sm text-primary/70">
                    O envio usa o produto retornado por service-plans e o servicePlanId selecionado nesta sessao.
                  </div>
                </div>
                {protocol ? (
                  <div className="rounded-lg border border-secondary bg-secondary/5 p-5">
                    <CheckCircle2 className="h-8 w-8 text-secondary" />
                    <h3 className="mt-3 text-xl font-extrabold text-primary">Solicitacao enviada</h3>
                    <p className="mt-2 text-sm text-primary/70">
                      {protocol ? `Protocolo: ${protocol}` : 'A equipe KawaTech seguira com a validacao.'}
                    </p>
                  </div>
                ) : (
                  <Button type="button" className="w-full" onClick={submit} disabled={loading || !canSubmit}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Finalizar contratacao
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OnboardingPage;
